// Making a journal, at /journals/new.
//
// The cover is shown large and updates as the title is typed, because the thing being made is an
// object with a name printed on it, and choosing a binding for a notebook you cannot see is
// choosing a swatch. Ported from journals-web.jsx (NewJournal).

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useJournals } from './JournalsProvider';
import { Cover } from './Cover';
import { CoverPicker } from './CoverPicker';
import { DEFAULT_COVER_ID } from './covers';
import { IconChevronLeft, IconSpark } from '../components/Icons';
import { Notice } from '../components/ui';

export function NewJournalScreen() {
  const { create } = useJournals();
  const navigate = useNavigate();

  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [coverId, setCoverId] = useState(DEFAULT_COVER_ID);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (!title.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const made = await create({ title: title.trim(), subtitle: subtitle.trim(), coverId });
      // Replace, so Back from the new journal returns to the shelf rather than to the form that
      // would make a second one.
      navigate(`/journals/${made.id}`, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not make that journal.');
      setBusy(false);
    }
  }

  return (
    <>
      <div className="jpage-head">
        <button className="jback" onClick={() => navigate('/journals')} aria-label="Back to journals">
          <IconChevronLeft />
        </button>
        <span className="ttl">New journal</span>
      </div>

      {error ? <Notice tone="danger">{error}</Notice> : null}

      <div className="wsplit">
        <div className="nc-preview">
          <Cover
            cover={coverId}
            title={title.trim() || 'Untitled journal'}
            subtitle={subtitle.trim()}
            size="lg"
          />
        </div>

        <div>
          <div className="nc-field">
            <label className="nc-label" htmlFor="journal-title">
              Title
            </label>
            <input
              id="journal-title"
              className="nc-in big"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void submit();
              }}
              placeholder="10 Days of Darkness"
              autoFocus
            />
          </div>

          <div className="nc-field">
            <label className="nc-label" htmlFor="journal-subtitle">
              Subtitle <span className="hint">optional</span>
            </label>
            <input
              id="journal-subtitle"
              className="nc-in"
              value={subtitle}
              onChange={(e) => setSubtitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void submit();
              }}
              placeholder="March 2026"
            />
          </div>

          <CoverPicker coverId={coverId} onPick={setCoverId} />

          <button
            className="nc-create"
            style={{ marginTop: 8 }}
            disabled={!title.trim() || busy}
            onClick={() => void submit()}
          >
            <IconSpark /> {busy ? 'Making it…' : 'Create journal'}
          </button>
        </div>
      </div>

      <div style={{ height: 60 }} />
    </>
  );
}
